import {
  ArrowLeft,
  ClipboardList,
  Eye,
  HelpCircle,
  History,
  LayoutGrid,
  Loader2,
  Upload,
} from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "components/common/Button";
import { MoncoLogoLink } from "components/common/MoncoLogoLink";
import { ONBOARDING_ANCHORS } from "./Onboarding/onboardingSelectors";
import { UndoRedoButton } from "./UndoRedoButton";
import type { UseOwnUndoRedoResult } from "./HistoryDrawer/hooks/useOwnUndoRedo";

interface ReviewPageHeaderV3Props {
  onBack: () => void;
  projectName?: string;
  showResearchReportButton?: boolean;
  onViewResearchReport: () => void;
  onOpenOverview: () => void;
  // The same Export dropdown as in the overview modal, so exporting does not require opening it
  exportButton?: ReactNode;
  onOpenAdditionalUpload?: () => void;
  onViewUploadedFiles?: () => void;
  uploadedFilesCount?: number;
  onStartTour: () => void;
  onToggleHistory?: () => void;
  // Omitted where the user cannot undo plan changes (read-only, no plan, diagrams, cable tab).
  undoRedo?: UseOwnUndoRedoResult;
}

export const ReviewPageHeaderV3 = ({
  onBack,
  projectName,
  showResearchReportButton,
  onViewResearchReport,
  onOpenOverview,
  exportButton,
  onOpenAdditionalUpload,
  onViewUploadedFiles,
  uploadedFilesCount = 0,
  onStartTour,
  onToggleHistory,
  undoRedo,
}: ReviewPageHeaderV3Props) => {
  const { t } = useTranslation();

  return (
    <div className="bg-white border-b border-gray-200 px-6 py-2 flex items-center justify-between">
      <div className="flex items-center gap-4">
        <MoncoLogoLink className="h-7" />
        <div className="h-5 w-px bg-gray-300" />
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 transition-colors"
        >
          <ArrowLeft size={16} />
          <span className="font-medium">{t("smartSurveyor.common.back")}</span>
        </button>
        {projectName && (
          <>
            <div className="h-5 w-px bg-gray-300" />
            <span className="text-sm font-medium text-gray-700 truncate max-w-xs">
              {projectName}
            </span>
          </>
        )}
      </div>
      <div
        data-onboarding={ONBOARDING_ANCHORS.headerActions}
        className="flex items-center gap-3"
      >
        <div className="flex items-center">
          {undoRedo && <UndoRedoButton action={undoRedo.undo} icon="undo" />}
          {onToggleHistory && (
            <Button
              variant="text"
              onClick={onToggleHistory}
              className="!px-1.5"
              startAdornment={<History size={18} />}
              title={t("smartSurveyor.history.tooltip")}
            >
              {t("smartSurveyor.history.title")}
            </Button>
          )}
          {undoRedo && <UndoRedoButton action={undoRedo.redo} icon="redo" />}
        </div>
        {onViewUploadedFiles && uploadedFilesCount > 0 && (
          <Button
            variant="warning"
            onClick={onViewUploadedFiles}
            className="gap-2 cursor-pointer rounded-full border border-amber-200"
            startAdornment={
              <Loader2 size={16} className="animate-spin text-amber-600" />
            }
            endAdornment={<Eye size={16} className="text-amber-600" />}
          >
            {t("smartSurveyor.reviewPage.analyzingNewFiles")} (
            {uploadedFilesCount})
          </Button>
        )}
        {onOpenAdditionalUpload && (
          <Button
            onClick={onOpenAdditionalUpload}
            className="flex items-center gap-2"
          >
            <Upload size={18} />
            {t("smartSurveyor.reviewPage.uploadMore")}
          </Button>
        )}
        {showResearchReportButton && (
          <Button
            onClick={onViewResearchReport}
            className="flex items-center gap-2"
          >
            <ClipboardList size={18} />
            {t("smartSurveyor.reviewPage.viewResearchReport")}
          </Button>
        )}
        <Button
          variant="primary"
          onClick={onOpenOverview}
          className="flex items-center gap-2"
        >
          <LayoutGrid size={18} />
          {t("smartSurveyor.reviewPage.overview")}
        </Button>
        {exportButton}
        <Button
          variant="text"
          onClick={onStartTour}
          startAdornment={<HelpCircle size={18} />}
          title={t("smartSurveyor.onboarding.helpButton.tooltip")}
        >
          {t("smartSurveyor.onboarding.helpButton.label")}
        </Button>
      </div>
    </div>
  );
};
